import { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL;
const API_KEY  = process.env.REACT_APP_API_KEY;
const POLL_MS  = 3000;

// ─── DESIGN SYSTEM — Notion-inspired ──────────────────────────────────────────
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font);
    font-size: 14px;
    line-height: 1.6;
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    transition: background 0.12s, color 0.12s;
  }

  :root[data-theme="light"] {
    --bg:       #ffffff;
    --surface:  #ffffff;
    --surface2: #f7f7f5;
    --border:   #e9e9e7;
    --border2:  #d3d3cf;
    --text:     #1a1a1a;
    --text2:    #555550;
    --muted:    #9b9b94;
    --accent:   #1a1a1a;
    --accent-fg:#ffffff;
    --green:    #2f9e44;
    --green-bg: #ebfbee;
    --red:      #c92a2a;
    --red-bg:   #fff5f5;
    --orange:   #d9480f;
    --shadow-sm:0 1px 3px rgba(0,0,0,0.06),0 0 0 1px rgba(0,0,0,0.04);
    --shadow:   0 2px 8px rgba(0,0,0,0.08),0 0 0 1px rgba(0,0,0,0.04);
    --font:     'DM Sans', sans-serif;
    --mono:     'DM Mono', monospace;
    --r:        5px;
    --r-lg:     8px;
  }

  :root[data-theme="dark"] {
    --bg:       #191919;
    --surface:  #212121;
    --surface2: #2a2a2a;
    --border:   #363636;
    --border2:  #484848;
    --text:     #e2e2e0;
    --text2:    #a0a09a;
    --muted:    #666660;
    --accent:   #e2e2e0;
    --accent-fg:#191919;
    --green:    #40c057;
    --green-bg: #1a2e1f;
    --red:      #ff6b6b;
    --red-bg:   #2e1a1a;
    --orange:   #ff9950;
    --shadow-sm:0 1px 3px rgba(0,0,0,0.2),0 0 0 1px rgba(255,255,255,0.03);
    --shadow:   0 2px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(255,255,255,0.03);
    --font:     'DM Sans', sans-serif;
    --mono:     'DM Mono', monospace;
    --r:        5px;
    --r-lg:     8px;
  }

  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 99px; }

  input, textarea, select {
    width: 100%; background: var(--surface2);
    border: 1px solid var(--border); color: var(--text);
    font-family: var(--font); font-size: 14px; font-weight: 400;
    padding: 7px 10px; border-radius: var(--r); outline: none;
    transition: border-color 0.1s, box-shadow 0.1s;
  }
  input:focus, textarea:focus, select:focus {
    border-color: var(--border2);
    box-shadow: 0 0 0 2px rgba(0,0,0,0.05);
  }
  button {
    cursor: pointer; font-family: var(--font);
    font-weight: 500; border: none; border-radius: var(--r);
    transition: opacity 0.1s, background 0.1s;
  }
  button:hover:not(:disabled) { opacity: 0.85; }
  label {
    display: block; margin-bottom: 4px;
    font-size: 12px; font-weight: 500; color: var(--muted);
  }
  a { color: var(--text); text-decoration: underline; text-underline-offset: 2px; }
  .tag {
    display: inline-block; padding: 1px 6px; border-radius: 3px;
    font-size: 11px; font-weight: 500; font-family: var(--mono);
  }
`;

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────
const T_SHIRT = [
  {key:"S",base:1,color:"var(--green)"},
  {key:"M",base:3,color:"var(--orange)"},
  {key:"L",base:5,color:"var(--red)"},
];
const DEFAULT_CONVERSION=[{maxLoe:3,sp:1},{maxLoe:6,sp:2},{maxLoe:9,sp:3},{maxLoe:12,sp:5},{maxLoe:15,sp:8}];
const DEFAULT_CRITERIA=["Architecture","Complexité","Tests"];
const STATUS_META={
  active:   {label:"En cours",       dot:"var(--green)"},
  archived: {label:"Archivée",       dot:"var(--muted)"},
  discussion:{label:"Besoin d'info", dot:"var(--red)"},
};

// ─── UTILS ─────────────────────────────────────────────────────────────────────
function uid(){return Math.random().toString(36).slice(2,9)}
function loeTSP(loe,conv){const s=[...conv].sort((a,b)=>a.maxLoe-b.maxLoe);for(const r of s)if(loe<=r.maxLoe)return r.sp;return s[s.length-1]?.sp??"?"}
function hasDivergence(votes,n){for(let i=0;i<n;i++){const v=votes.map(v=>v.scores?.[i]).filter(Boolean);if(v.length<2)continue;const b=T_SHIRT.find(t=>t.key===v[0])?.base??0;for(const x of v)if(Math.abs((T_SHIRT.find(t=>t.key===x)?.base??0)-b)>=4)return true}return false}
function calcItemResult(item,votes,conv){if(!votes.length)return null;const avg=item.criteria.map((_,i)=>{const v=votes.map(v=>T_SHIRT.find(t=>t.key===v.scores?.[i])?.base??0);return Math.round(v.reduce((s,x)=>s+x,0)/v.length)});const totalLoe=avg.reduce((s,x)=>s+x,0);return{avg,totalLoe,sp:loeTSP(totalLoe,conv)}}
function makeInit(teamName=""){return{teamName,criteria:DEFAULT_CRITERIA.map((name,i)=>({id:i,name,labels:{S:"",M:"",L:""}})),conversion:DEFAULT_CONVERSION,items:[],mode:"async",resultsRevealed:false,participants:[],votes:{}}}

// ─── LOCALSTORAGE ──────────────────────────────────────────────────────────────
function lsGet(k){try{return JSON.parse(localStorage.getItem(k))}catch{return null}}
function lsSet(k,v){try{localStorage.setItem(k,JSON.stringify(v))}catch{}}
function lsDel(k){try{localStorage.removeItem(k)}catch{}}
function getSavedSession(){return lsGet("loe_current_session")}
function saveSession(d){lsSet("loe_current_session",d)}
function clearSession(){lsDel("loe_current_session")}
function getSavedName(sid){return lsGet(`loe_name_${sid}`)||""}
function saveName(sid,n){lsSet(`loe_name_${sid}`,n)}

// ─── API ───────────────────────────────────────────────────────────────────────
async function api(action,method="GET",body=null,sid=null){
  const url=new URL(API_BASE);
  url.searchParams.set("action",action);
  if(sid)url.searchParams.set("sid",sid);
  const res=await fetch(url.toString(),{method,headers:{"X-API-Key":API_KEY,...(body?{"Content-Type":"application/json"}:{})}, ...(body?{body:JSON.stringify(body)}:{})});
  if(!res.ok){const e=await res.json().catch(()=>({}));throw new Error(e.error||`HTTP ${res.status}`)}
  return res.json()
}

// ─── PRIMITIVES ────────────────────────────────────────────────────────────────
function Btn({children,onClick,variant="primary",size="md",style:s,disabled}){
  const vs={
    primary:{background:"var(--accent)",color:"var(--accent-fg)"},
    ghost:{background:"transparent",color:"var(--text2)",border:"1px solid var(--border)"},
    danger:{background:"var(--red-bg)",color:"var(--red)",border:"1px solid transparent"},
    success:{background:"var(--green-bg)",color:"var(--green)",border:"1px solid transparent"},
    subtle:{background:"var(--surface2)",color:"var(--text2)",border:"1px solid var(--border)"},
  };
  const sz={
    xs:{padding:"3px 8px",fontSize:"12px"},
    sm:{padding:"5px 10px",fontSize:"13px"},
    md:{padding:"7px 14px",fontSize:"14px"},
    lg:{padding:"10px 20px",fontSize:"15px"},
  };
  return <button onClick={onClick} disabled={disabled} style={{...vs[variant],...sz[size],opacity:disabled?0.4:1,...s}}>{children}</button>
}

function Card({children,style:s}){
  return <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:"16px",boxShadow:"var(--shadow-sm)",...s}}>{children}</div>
}

function Field({label,children,style:s}){
  return <div style={s}><label>{label}</label>{children}</div>
}

function Divider({label}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:12,margin:"4px 0"}}>
      <div style={{flex:1,height:1,background:"var(--border)"}}/>
      {label&&<span style={{fontSize:12,color:"var(--muted)",fontFamily:"var(--mono)"}}>{label}</span>}
      <div style={{flex:1,height:1,background:"var(--border)"}}/>
    </div>
  )
}

function SyncDot({status}){
  const c={synced:"var(--green)",syncing:"var(--orange)",error:"var(--red)"}[status]||"var(--green)";
  return <span style={{width:6,height:6,borderRadius:"50%",background:c,display:"inline-block",flexShrink:0}}/>
}

function StatusDot({status}){
  const m=STATUS_META[status]||STATUS_META.active;
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,color:"var(--text2)",fontWeight:500}}>
      <span style={{width:6,height:6,borderRadius:"50%",background:m.dot,display:"inline-block",flexShrink:0}}/>
      {m.label}
    </span>
  )
}

function CopyBtn({text,label="Copier"}){
  const [copied,setCopied]=useState(false);
  function copy(){navigator.clipboard.writeText(text).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000)})}
  return(
    <button onClick={copy} style={{
      background:copied?"var(--green-bg)":"var(--surface2)",
      color:copied?"var(--green)":"var(--muted)",
      border:"1px solid var(--border)",
      borderRadius:"var(--r)",padding:"5px 10px",
      fontSize:12,cursor:"pointer",transition:"all 0.15s",whiteSpace:"nowrap",fontFamily:"var(--font)",fontWeight:500,
    }}>
      {copied?"Copié":"Copier"}
    </button>
  )
}

function ConfirmModal({title,message,onConfirm,onClose}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={onClose}>
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:24,width:"100%",maxWidth:400,boxShadow:"var(--shadow)"}} onClick={e=>e.stopPropagation()}>
        <p style={{fontWeight:600,fontSize:15,marginBottom:8}}>{title}</p>
        <p style={{color:"var(--text2)",fontSize:13,marginBottom:20,lineHeight:1.5}}>{message}</p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Btn variant="ghost" size="sm" onClick={onClose}>Annuler</Btn>
          <Btn variant="danger" size="sm" onClick={()=>{onConfirm();onClose()}}>Supprimer</Btn>
        </div>
      </div>
    </div>
  )
}

// ─── LANDING PAGE ──────────────────────────────────────────────────────────────
// ─── IMAGES LANDING — modifiez chaque URL indépendamment ─────────────────────
const IMG = {
   hero:  "./assets/Smart-People-1--Streamline-Brooklyn.png",
  steps: "./assets/Work-Collaboration-1--Streamline-Brooklyn",
  feat1: "https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F0q2j19m4x61s31crnuco.jpg",
  feat2: "https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F0q2j19m4x61s31crnuco.jpg",
  feat3: "https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F0q2j19m4x61s31crnuco.jpg",
  feat4: "https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F0q2j19m4x61s31crnuco.jpg",
};

const LANDING_CSS = `
  .land-hero { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; max-width:1100px; margin:0 auto; padding:96px 48px 80px; }
  .land-steps { display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center; max-width:1100px; margin:0 auto; padding:0 48px 72px; }
  .land-features { display:grid; grid-template-columns:repeat(2,1fr); gap:1px; background:var(--border); border:1px solid var(--border); border-radius:var(--r-lg); overflow:hidden; }
  .land-nav { padding:0 48px; }
  .land-wrap { max-width:1100px; margin:0 auto; padding:72px 48px; }
  .land-cta { border-top:1px solid var(--border); padding:64px 48px; text-align:center; }
  .land-footer { border-top:1px solid var(--border); padding:16px 48px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:8px; }
  @media(max-width:768px){
    .land-hero { grid-template-columns:1fr; gap:36px; padding:56px 24px 48px; }
    .land-steps { grid-template-columns:1fr; gap:36px; padding:0 24px 48px; }
    .land-features { grid-template-columns:1fr; }
    .land-nav { padding:0 20px; }
    .land-wrap { padding:48px 24px; }
    .land-cta { padding:48px 24px; }
    .land-footer { padding:16px 24px; }
  }
`;

function LandingPage({onEnterApp}){
  const LIGHT={
    bg:"#ffffff",surface:"#ffffff",surface2:"#f7f7f5",border:"#e9e9e7",
    text:"#1a1a1a",text2:"#555550",muted:"#9b9b94",
    accent:"#1a1a1a",accentFg:"#ffffff",
  };
  const features=[
    {n:"1",title:"Sortez de l'abstraction numérique.",       body:"Évaluez chacun de vos critères en tailles S, M ou L. Une approche visuelle qui simplifie la prise de décision technique."},
    {n:"2",title:"Flexibilité Synchrone & Asynchrone.",       body:"Ne bloquez plus les agendas. Votez en direct lors de vos rituels ou laissez l'équipe contribuer en asynchrone pour libérer du temps de focus."},
    {n:"3",title:"Détection intelligente des zones d'ombre.", body:"L'outil repère automatiquement les divergences d'opinions. Ne perdez plus de temps sur les consensus, discutez uniquement là où c'est nécessaire."},
    {n:"4",title:"De la logique au backlog.",                  body:"Vos critères sont instantanément convertis en Story Points selon une matrice logique paramétrable. Une passerelle directe vers la planification de sprint."},
  ];
  const steps=[
    ["1","Configurez & Invitez","Créez votre session en un clic et envoyez le lien d'accès à votre équipe de développement."],
    ["2","Cadrez les critères","Sélectionnez les dimensions à évaluer (Technique, Data, Compliance...) pour guider la réflexion."],
    ["3","Votez en toute liberté","Chaque membre évalue l'effort selon sa vision, en direct ou au moment qui lui convient le mieux."],
    ["4","Analysez & Ajustez","Visualisez les résultats, affinez les points de friction et validez vos estimations finales avec confiance."],
  ];
  return(
    <div style={{minHeight:"100vh",background:LIGHT.bg,color:LIGHT.text,fontFamily:"var(--font)"}}>
      <style>{LANDING_CSS}</style>

      {/* Nav — toujours clair */}
      <nav style={{position:"sticky",top:0,zIndex:50,borderBottom:`1px solid ${LIGHT.border}`,background:LIGHT.bg,height:52,display:"flex",alignItems:"center",justifyContent:"space-between"}} className="land-nav">
        <span style={{fontFamily:"var(--mono)",fontSize:13,marginRight:24}}>
          <span style={{fontWeight:300,color:LIGHT.text2}}>esti</span><span style={{fontWeight:700,color:LIGHT.text}}>mate</span>
        </span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={onEnterApp} style={{background:"transparent",color:LIGHT.text2,border:`1px solid ${LIGHT.border}`,borderRadius:"var(--r)",padding:"5px 10px",fontSize:13,cursor:"pointer",fontFamily:"var(--font)",fontWeight:500}}>Se connecter</button>
          <button onClick={onEnterApp} style={{background:LIGHT.accent,color:LIGHT.accentFg,border:"none",borderRadius:"var(--r)",padding:"5px 10px",fontSize:13,cursor:"pointer",fontFamily:"var(--font)",fontWeight:500}}>Commencer →</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="land-hero">
        <div>
          <p style={{fontSize:11,fontWeight:500,color:LIGHT.muted,fontFamily:"var(--mono)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:20}}>Outil d'estimation agile</p>
          <h1 style={{fontSize:"clamp(28px,4.5vw,46px)",lineHeight:1.15,letterSpacing:"-0.03em",color:LIGHT.text,marginBottom:20}}>
            <span style={{fontWeight:300}}>L'estimation agile,</span><br/>
            <span style={{fontWeight:700}}>sans le doigt mouillé.</span>
          </h1>
          <p style={{fontSize:15,color:LIGHT.text2,lineHeight:1.7,maxWidth:400,marginBottom:36,fontWeight:300}}>
            Transformez le ressenti technique en données exploitables pour garantir des engagements réalistes et une prédictibilité immédiate.
          </p>
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <button onClick={onEnterApp} style={{background:LIGHT.accent,color:LIGHT.accentFg,border:"none",borderRadius:"var(--r)",padding:"10px 20px",fontSize:15,cursor:"pointer",fontFamily:"var(--font)",fontWeight:500}}>Lancer une session gratuitement</button>
          </div>
        </div>
        <img src={IMG.hero} alt="Aperçu de l'application" style={{width:"100%",borderRadius:12,display:"block",maxHeight:380,objectFit:"cover"}}/>
      </section>

      <div style={{borderTop:`1px solid ${LIGHT.border}`,maxWidth:1100,margin:"0 auto"}}/>

      {/* Features */}
      <section className="land-wrap">
        <div style={{marginBottom:40}}>
          <span style={{display:"inline-block",background:LIGHT.surface2,border:`1px solid ${LIGHT.border}`,borderRadius:4,padding:"2px 10px",fontSize:12,fontWeight:500,color:LIGHT.text2,fontFamily:"var(--mono)",marginBottom:14}}>Les bénéfices</span>
          <p style={{fontSize:14,color:LIGHT.muted,maxWidth:400,lineHeight:1.6,fontWeight:300}}>Un flux simple du vote à la décision, sans friction.</p>
        </div>
        <div className="land-features">
          {features.map((f,i)=>(
            <div key={i} style={{background:LIGHT.surface,padding:"28px 24px"}}>
              <div style={{
                width:40,height:40,borderRadius:"50%",
                background:LIGHT.surface2,border:`1px solid ${LIGHT.border}`,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontFamily:"var(--mono)",fontSize:16,fontWeight:700,color:LIGHT.text,
                marginBottom:16,flexShrink:0,
              }}>{f.n}</div>
              <p style={{fontWeight:600,fontSize:14,color:LIGHT.text,marginBottom:8,lineHeight:1.4}}>{f.title}</p>
              <p style={{fontSize:13,color:LIGHT.text2,lineHeight:1.65,fontWeight:300}}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="land-steps">
        <img src={IMG.steps} alt="Illustration" style={{width:"100%",borderRadius:12,display:"block",maxHeight:340,objectFit:"cover"}}/>
        <div>
          <p style={{fontSize:11,fontWeight:500,color:LIGHT.muted,fontFamily:"var(--mono)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:24}}>Comment ça marche ?</p>
          <div style={{display:"flex",flexDirection:"column",gap:24}}>
            {steps.map(([n,t,d])=>(
              <div key={n} style={{display:"flex",gap:16,alignItems:"flex-start"}}>
                <div style={{
                  width:28,height:28,borderRadius:"50%",flexShrink:0,
                  background:LIGHT.surface2,border:`1px solid ${LIGHT.border}`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:LIGHT.text,
                  marginTop:1,
                }}>{n}</div>
                <div>
                  <p style={{fontWeight:600,fontSize:14,color:LIGHT.text,marginBottom:4}}>{t}</p>
                  <p style={{fontSize:13,color:LIGHT.text2,fontWeight:300,lineHeight:1.6}}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{borderTop:`1px solid ${LIGHT.border}`,padding:"64px 48px",textAlign:"center",background:LIGHT.bg}} className="land-cta">
        <h2 style={{fontSize:"clamp(20px,4vw,28px)",fontWeight:300,letterSpacing:"-0.02em",marginBottom:4,color:LIGHT.text}}>
          Prêt à estimer <span style={{fontWeight:700}}>autrement ?</span>
        </h2>
        <p style={{color:LIGHT.muted,fontSize:14,marginBottom:28,fontWeight:300}}>Créez une session en 30 secondes. Aucune installation requise.</p>
        <button onClick={onEnterApp} style={{background:LIGHT.accent,color:LIGHT.accentFg,border:"none",borderRadius:"var(--r)",padding:"10px 20px",fontSize:15,cursor:"pointer",fontFamily:"var(--font)",fontWeight:500}}>Lancer une session gratuitement</button>
      </section>

      {/* Footer */}
      <footer style={{borderTop:`1px solid ${LIGHT.border}`,background:LIGHT.bg}} className="land-footer">
        <span style={{fontFamily:"var(--mono)",fontSize:12,color:LIGHT.muted}}>
          <span style={{fontWeight:300}}>esti</span><span style={{fontWeight:700}}>mate</span>
        </span>
        <a href="https://www.linkedin.com/in/adrienpegny/" target="_blank" rel="noreferrer" style={{fontSize:12,color:LIGHT.muted,textDecoration:"none"}}>Créé par Adrien Pegny</a>
      </footer>
    </div>
  )
}

// ─── HOME PAGE ─────────────────────────────────────────────────────────────────
function HomePage({onJoin,onCreate,onBack}){
  const [token,setToken]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  useEffect(()=>{const s=new URLSearchParams(window.location.search).get("s");if(s)setToken(s)},[]);
  async function go(){if(!token.trim()){setErr("Code requis.");return}setLoading(true);setErr("");try{const r=await api("check","GET",null,token.trim());onJoin(token.trim(),r.name)}catch{setErr("Session introuvable.")}finally{setLoading(false)}}
  return(
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:380}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"var(--muted)",fontSize:13,marginBottom:32,cursor:"pointer",display:"flex",alignItems:"center",gap:6,padding:0}}>← Accueil</button>
        <h1 style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",marginBottom:6}}>Rejoindre une session</h1>
        <p style={{color:"var(--text2)",fontSize:13,marginBottom:28,fontWeight:300}}>Entrez le code fourni par votre administrateur.</p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Code de session">
            <input value={token} onChange={e=>setToken(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="ex: 3f8a1b2c" autoFocus/>
          </Field>
          {err&&<p style={{color:"var(--red)",fontSize:12}}>{err}</p>}
          <Btn variant="primary" size="md" onClick={go} disabled={loading} style={{width:"100%"}}>{loading?"Vérification...":"Rejoindre →"}</Btn>
          <Divider label="ou"/>
          <Btn variant="ghost" size="md" onClick={onCreate} style={{width:"100%"}}>Créer une nouvelle session</Btn>
        </div>
      </div>
    </div>
  )
}

// ─── CREATE SESSION ────────────────────────────────────────────────────────────
function CreateSessionPage({onCreated,onBack}){
  const [name,setName]=useState("");const [password,setPassword]=useState("");const [adminKey,setAdminKey]=useState("");
  const [loading,setLoading]=useState(false);const [err,setErr]=useState("");const [result,setResult]=useState(null);
  async function go(){
    if(!name.trim()||!password.trim()||!adminKey.trim()){setErr("Tous les champs sont requis.");return}
    if(password===adminKey){setErr("Mot de passe votant et clé admin doivent être différents.");return}
    setLoading(true);setErr("");
    try{setResult(await api("create","POST",{name:name.trim(),password:password.trim(),adminKey:adminKey.trim()}))}
    catch(e){setErr("Erreur : "+e.message)}finally{setLoading(false)}
  }
  if(result){
    const base=`${window.location.origin}${window.location.pathname}`;
    const voterUrl=`${base}?s=${result.token}`;
    const adminUrl=`${base}?s=${result.token}&role=admin`;
    return(
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:480}}>
          <Card style={{padding:28}}>
            <p style={{fontWeight:600,fontSize:16,marginBottom:4}}>Session créée</p>
            <p style={{color:"var(--text2)",fontSize:13,marginBottom:24,fontWeight:300}}>{result.name}</p>
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div>
                <label>Code de session</label>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <code style={{fontFamily:"var(--mono)",fontSize:18,fontWeight:500,color:"var(--text)",padding:"8px 12px",background:"var(--surface2)",borderRadius:"var(--r)",border:"1px solid var(--border)",flex:1,letterSpacing:"0.05em"}}>{result.token}</code>
                  <CopyBtn text={result.token}/>
                </div>
              </div>
              <div>
                <label>Lien votants</label>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <code style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--green)",background:"var(--green-bg)",padding:"8px 10px",borderRadius:"var(--r)",border:"1px solid var(--border)",wordBreak:"break-all",flex:1,lineHeight:1.5}}>{voterUrl}</code>
                  <CopyBtn text={voterUrl}/>
                </div>
                <p style={{fontSize:11,color:"var(--muted)",marginTop:4}}>À partager avec l'équipe avec le mot de passe votant</p>
              </div>
              <div>
                <label>Lien admin</label>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <code style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",background:"var(--surface2)",padding:"8px 10px",borderRadius:"var(--r)",border:"1px solid var(--border)",wordBreak:"break-all",flex:1,lineHeight:1.5}}>{adminUrl}</code>
                  <CopyBtn text={adminUrl}/>
                </div>
                <p style={{fontSize:11,color:"var(--muted)",marginTop:4}}>Privé — nécessite la clé admin</p>
              </div>
              <div style={{padding:"10px 12px",background:"var(--red-bg)",border:"1px solid transparent",borderRadius:"var(--r)",fontSize:12,color:"var(--red)"}}>
                Notez votre clé admin — elle ne peut pas être récupérée.
              </div>
              <Btn variant="primary" size="md" onClick={()=>onCreated(result.token,result.name)} style={{width:"100%"}}>Accéder à la session →</Btn>
            </div>
          </Card>
        </div>
      </div>
    )
  }
  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:380}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"var(--muted)",fontSize:13,marginBottom:32,cursor:"pointer",padding:0}}>← Retour</button>
        <h1 style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",marginBottom:6}}>Nouvelle session</h1>
        <p style={{color:"var(--text2)",fontSize:13,marginBottom:28,fontWeight:300}}>Un espace isolé pour votre équipe.</p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Nom de l'équipe"><input value={name} onChange={e=>setName(e.target.value)} placeholder="Ex: Squad Phoenix" autoFocus/></Field>
          <Field label="Mot de passe votants"><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Partagé avec l'équipe"/></Field>
          <Field label="Clé admin (privée)"><input type="password" value={adminKey} onChange={e=>setAdminKey(e.target.value)} placeholder="Réservée à l'administrateur"/></Field>
          <p style={{fontSize:12,color:"var(--muted)",fontWeight:300}}>Le mot de passe votant est partagé. La clé admin est strictement privée.</p>
          {err&&<p style={{color:"var(--red)",fontSize:12}}>{err}</p>}
          <Btn variant="primary" size="md" onClick={go} disabled={loading} style={{width:"100%"}}>{loading?"Création...":"Créer →"}</Btn>
        </div>
      </div>
    </div>
  )
}

// ─── LOGIN ─────────────────────────────────────────────────────────────────────
function LoginPage({sessionId,sessionName,onLogin,onBack}){
  const [password,setPassword]=useState("");
  const [name,setName]=useState(getSavedName(sessionId));
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  async function go(){
    if(!name.trim()||!password.trim()){setErr("Nom et mot de passe requis.");return}
    setLoading(true);setErr("");
    try{
      const r=await api("verify","POST",{password:password.trim(),adminKey:password.trim()},sessionId);
      saveName(sessionId,name.trim());
      onLogin({id:uid(),name:name.trim(),role:r.role});
    }catch{setErr("Identifiants incorrects.")}
    finally{setLoading(false)}
  }
  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:360}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"var(--muted)",fontSize:13,marginBottom:32,cursor:"pointer",padding:0}}>← Changer de session</button>
        <p style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--muted)",letterSpacing:"0.04em",marginBottom:8}}>{sessionId}</p>
        <h1 style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",marginBottom:28}}>{sessionName}</h1>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Votre prénom">
            <input value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="Jean-Pierre" autoFocus={!name}/>
            {name&&<p style={{fontSize:11,color:"var(--green)",marginTop:3}}>Mémorisé sur cet appareil</p>}
          </Field>
          <Field label="Mot de passe ou clé admin">
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="••••••••"/>
          </Field>
          <p style={{fontSize:12,color:"var(--muted)",fontWeight:300}}>Votant : mot de passe équipe · Admin : clé admin privée</p>
          {err&&<p style={{color:"var(--red)",fontSize:12}}>{err}</p>}
          <Btn variant="primary" size="md" onClick={go} disabled={loading} style={{width:"100%"}}>{loading?"Connexion...":"Accéder →"}</Btn>
        </div>
      </div>
    </div>
  )
}

// ─── TOP NAV ───────────────────────────────────────────────────────────────────
function TopNav({user,sessionId,view,setView,onLogout,syncStatus,theme,onToggleTheme}){
  const [showInvite,setShowInvite]=useState(false);
  const navItems=user.role==="admin"
    ?[{id:"board",label:"Tableau"},{id:"config",label:"Configuration"},{id:"archive",label:"Archives"}]
    :[{id:"board",label:"Voter"}];
  const base=`${window.location.origin}${window.location.pathname}`;
  const voterUrl=`${base}?s=${sessionId}`;
  return(
    <>
      <div style={{borderBottom:"1px solid var(--border)",background:"var(--bg)",padding:"0 20px",display:"flex",alignItems:"center",position:"sticky",top:0,zIndex:100,height:44}}>
        <span style={{fontFamily:"var(--mono)",fontSize:13,marginRight:24,flexShrink:0}}><span style={{fontWeight:300,color:"var(--text2)"}}>esti</span><span style={{fontWeight:700,color:"var(--text)"}}>mate</span></span>
        <div style={{display:"flex",flex:1,height:"100%"}}>
          {navItems.map(n=>(
            <button key={n.id} onClick={()=>setView(n.id)} style={{
              height:"100%",padding:"0 14px",
              background:"transparent",color:view===n.id?"var(--text)":"var(--muted)",
              border:"none",borderBottom:view===n.id?"2px solid var(--text)":"2px solid transparent",
              fontFamily:"var(--font)",fontWeight:view===n.id?500:400,fontSize:13,
              cursor:"pointer",transition:"all 0.1s",
            }}>{n.label}</button>
          ))}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <SyncDot status={syncStatus}/>
          <button onClick={()=>setShowInvite(v=>!v)} style={{background:"none",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"4px 8px",fontSize:12,cursor:"pointer",color:"var(--text2)",fontFamily:"var(--font)",fontWeight:500}}>Inviter</button>
          <button onClick={onToggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"4px 8px",fontSize:12,cursor:"pointer",color:"var(--text2)"}}>
            {theme==="dark"?"Clair":"Sombre"}
          </button>
          <span style={{fontSize:12,color:"var(--muted)",padding:"0 4px"}}>{user.name}</span>
          <button onClick={onLogout} style={{background:"none",border:"none",color:"var(--muted)",fontSize:12,cursor:"pointer",fontFamily:"var(--font)"}}>Quitter</button>
        </div>
      </div>
      {showInvite&&(
        <div style={{background:"var(--surface2)",borderBottom:"1px solid var(--border)",padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:"var(--text2)",fontWeight:500,flexShrink:0}}>Lien d'invitation</span>
          <code style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{voterUrl}</code>
          <CopyBtn text={voterUrl} label="Copier le lien"/>
          <button onClick={()=>setShowInvite(false)} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,lineHeight:1,flexShrink:0}}>✕</button>
        </div>
      )}
    </>
  )
}

// ─── VOTE PANEL ────────────────────────────────────────────────────────────────
function VotePanel({item,myVote,onVote}){
  const [editing,setEditing]=useState(!myVote);
  const [scores,setScores]=useState(myVote?.scores??item.criteria.map(()=>null));
  useEffect(()=>{if(myVote&&!editing)setScores(myVote.scores)},[myVote]);

  if(myVote&&!editing){
    return(
      <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--border)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:12,color:"var(--green)",fontWeight:500}}>Vote soumis</span>
          <Btn variant="subtle" size="xs" onClick={()=>setEditing(true)}>Modifier mon vote</Btn>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {item.criteria.map((c,i)=>{
            const key=myVote.scores[i];
            const t=T_SHIRT.find(t=>t.key===key);
            return(
              <div key={i} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"6px 12px",minWidth:80,textAlign:"center"}}>
                <p style={{fontSize:11,color:"var(--muted)",marginBottom:2}}>{c.name}</p>
                <p style={{fontFamily:"var(--mono)",fontSize:16,fontWeight:500,color:t?.color??"var(--text)"}}>{key}</p>
                {c.labels?.[key]&&<p style={{fontSize:10,color:"var(--muted)",marginTop:1}}>{c.labels[key]}</p>}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return(
    <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--border)"}}>
      <p style={{fontSize:12,color:"var(--muted)",marginBottom:12}}>{myVote?"Modifier votre vote":"Votre estimation"}</p>
      <div style={{display:"flex",flexWrap:"wrap",gap:16,marginBottom:14}}>
        {item.criteria.map((c,i)=>(
          <div key={c.id??i}>
            <p style={{fontSize:11,color:"var(--muted)",marginBottom:6,fontWeight:500}}>{c.name}</p>
            <div style={{display:"flex",gap:4}}>
              {T_SHIRT.map(t=>(
                <button key={t.key} onClick={()=>setScores(prev=>{const n=[...prev];n[i]=t.key;return n})} style={{
                  width:38,height:38,
                  background:scores[i]===t.key?"var(--accent)":"var(--surface2)",
                  color:scores[i]===t.key?"var(--accent-fg)":t.color,
                  border:`1px solid ${scores[i]===t.key?"var(--accent)":"var(--border)"}`,
                  borderRadius:"var(--r)",
                  fontFamily:"var(--mono)",fontWeight:500,fontSize:13,
                  cursor:"pointer",transition:"all 0.1s",
                }}>{t.key}</button>
              ))}
            </div>
            {c.labels&&(
              <div style={{marginTop:3,display:"flex",gap:4}}>
                {T_SHIRT.map(t=><div key={t.key} style={{fontSize:9,color:"var(--muted)",width:38,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={c.labels[t.key]}>{c.labels[t.key]}</div>)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <Btn variant="primary" size="sm" onClick={()=>{onVote(scores);setEditing(false)}} disabled={scores.some(s=>s===null)}>
          {myVote?"Sauvegarder":"Soumettre"}
        </Btn>
        {myVote&&<Btn variant="ghost" size="sm" onClick={()=>{setScores(myVote.scores);setEditing(false)}}>Annuler</Btn>}
      </div>
    </div>
  )
}

// ─── ITEM CARD ─────────────────────────────────────────────────────────────────
function ItemCard({item,votes,conversion,revealed,isAdmin,onArchive,onDelete,onEdit,onMarkDiscussion,onMarkActive,onVote,userId,mode}){
  const [showVotes,setShowVotes]=useState(false);
  const [confirmDelete,setConfirmDelete]=useState(false);
  const result=revealed?calcItemResult(item,votes,conversion):null;
  const divergence=votes.length>=2&&hasDivergence(votes,item.criteria.length);
  const effectiveStatus=divergence&&revealed?"discussion":item.status;
  const myVote=votes.find(v=>v.userId===userId);
  const canSeeResults=isAdmin||(mode==="live"?revealed:!!myVote);

  return(
    <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:"16px",boxShadow:"var(--shadow-sm)"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:item.description||item.link?10:0}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:4}}>
            <StatusDot status={effectiveStatus}/>
            {divergence&&revealed&&<span className="tag" style={{background:"var(--red-bg)",color:"var(--red)"}}>Divergence</span>}
            {myVote&&<span className="tag" style={{background:"var(--green-bg)",color:"var(--green)"}}>Voté</span>}
          </div>
          <h3 style={{fontWeight:600,fontSize:15,color:"var(--text)",lineHeight:1.3}}>{item.title}</h3>
        </div>
        {result&&canSeeResults&&(
          <div style={{textAlign:"center",background:"var(--surface2)",borderRadius:"var(--r)",padding:"8px 14px",border:"1px solid var(--border)",flexShrink:0}}>
            <p style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)",marginBottom:2}}>Score / SP</p>
            <p style={{fontSize:22,fontWeight:600,color:"var(--text)",lineHeight:1,fontFamily:"var(--mono)"}}>{result.totalLoe}</p>
            <p style={{fontSize:12,color:"var(--green)",fontWeight:500}}>→ {result.sp} SP</p>
          </div>
        )}
        {!canSeeResults&&!myVote&&mode==="async"&&(
          <div style={{textAlign:"center",background:"var(--surface2)",borderRadius:"var(--r)",padding:"8px 14px",border:"1px solid var(--border)",flexShrink:0,opacity:0.5}}>
            <p style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)"}}>Votez pour voir</p>
          </div>
        )}
      </div>

      {item.description&&<p style={{fontSize:13,color:"var(--text2)",marginBottom:6,lineHeight:1.55,fontWeight:300}}>{item.description}</p>}
      {item.link&&<a href={item.link} target="_blank" rel="noreferrer" style={{fontSize:12,fontFamily:"var(--mono)",color:"var(--text2)"}}>{item.link}</a>}

      {/* Récap votes */}
      {canSeeResults&&revealed&&votes.length>0&&(
        <div style={{marginTop:12}}>
          <button onClick={()=>setShowVotes(v=>!v)} style={{background:"none",border:"none",padding:0,fontSize:12,color:"var(--muted)",cursor:"pointer",fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:4}}>
            <span>{showVotes?"▼":"▶"}</span>
            <span>{votes.length} vote{votes.length>1?"s":""}{result?` · Score ${result.totalLoe}`:""}</span>
          </button>
          {showVotes&&(
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:5}}>
              {votes.map(v=>(
                <div key={v.userId} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"var(--text2)",fontWeight:500,minWidth:100,fontFamily:"var(--mono)"}}>{v.userName}</span>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {v.scores.map((s,i)=>{
                      const t=T_SHIRT.find(t=>t.key===s);
                      return <span key={i} className="tag" style={{background:"var(--surface2)",color:t?.color??"var(--text2)",border:"1px solid var(--border)"}}>{item.criteria[i]?.name} {s}</span>
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {onVote&&<VotePanel item={item} myVote={myVote} onVote={onVote}/>}

      {/* Besoin d'info — votants */}
      {onVote&&!isAdmin&&(
        <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>
          {effectiveStatus!=="discussion"
            ?<button onClick={onMarkDiscussion} style={{background:"none",border:"none",padding:0,fontSize:12,color:"var(--muted)",cursor:"pointer",fontFamily:"var(--font)"}}>Signaler un besoin d'information</button>
            :<button onClick={onMarkActive} style={{background:"none",border:"none",padding:0,fontSize:12,color:"var(--muted)",cursor:"pointer",fontFamily:"var(--font)"}}>Retirer le signalement</button>
          }
        </div>
      )}

      {/* Actions admin */}
      {isAdmin&&(
        <div style={{display:"flex",gap:6,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)",flexWrap:"wrap"}}>
          <Btn variant="subtle" size="xs" onClick={onEdit}>Modifier la fiche</Btn>
          <Btn variant="subtle" size="xs" onClick={onArchive}>Archiver</Btn>
          {effectiveStatus!=="discussion"
            ?<Btn variant="subtle" size="xs" onClick={onMarkDiscussion} style={{color:"var(--red)"}}>Besoin d'information</Btn>
            :<Btn variant="subtle" size="xs" onClick={onMarkActive}>Retirer le flag</Btn>
          }
          <Btn variant="danger" size="xs" onClick={()=>setConfirmDelete(true)}>Supprimer</Btn>
        </div>
      )}

      {confirmDelete&&<ConfirmModal title={`Supprimer "${item.title}" ?`} message="Cette fiche et tous ses votes seront définitivement supprimés." onConfirm={onDelete} onClose={()=>setConfirmDelete(false)}/>}
    </div>
  )
}

// ─── ITEM MODAL ────────────────────────────────────────────────────────────────
function ItemModal({initial,onSave,onClose}){
  const [title,setTitle]=useState(initial?.title??"");
  const [desc,setDesc]=useState(initial?.description??"");
  const [link,setLink]=useState(initial?.link??"");
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={onClose}>
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:24,width:"100%",maxWidth:500,boxShadow:"var(--shadow)"}} onClick={e=>e.stopPropagation()}>
        <p style={{fontWeight:600,fontSize:15,marginBottom:20}}>{initial?"Modifier la fiche":"Nouvelle fiche"}</p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Titre"><input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ex: Refonte API Auth" autoFocus/></Field>
          <Field label="Description"><textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3} style={{resize:"vertical"}}/></Field>
          <Field label="Lien externe"><input value={link} onChange={e=>setLink(e.target.value)} placeholder="https://..."/></Field>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:4}}>
            <Btn variant="ghost" size="sm" onClick={onClose}>Annuler</Btn>
            <Btn variant="primary" size="sm" disabled={!title.trim()} onClick={()=>onSave({title,description:desc,link})}>{initial?"Mettre à jour":"Créer"}</Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── JIRA IMPORT ───────────────────────────────────────────────────────────────
function adfToText(node){
  if(!node)return "";
  if(typeof node==="string")return node;
  if(node.type==="text")return node.text??"";
  if(Array.isArray(node.content))return node.content.map(adfToText).join(" ").replace(/\s+/g," ").trim();
  return "";
}

function parseJiraJson(raw,jiraBaseUrl){
  let issues=[];
  if(Array.isArray(raw))issues=raw;
  else if(raw?.issues&&Array.isArray(raw.issues))issues=raw.issues;
  else throw new Error("Format non reconnu. Le JSON doit contenir un tableau 'issues' ou être un tableau direct.");
  if(issues.length===0)throw new Error("Aucune issue trouvée dans le fichier.");
  return issues.map(issue=>{
    const key=issue.key??issue.id??"???";
    const fields=issue.fields??issue;
    const summary=fields.summary??fields.title??"(sans titre)";
    let description="";
    if(typeof fields.description==="string")description=fields.description;
    else if(fields.description?.content)description=adfToText(fields.description);
    const base=jiraBaseUrl?jiraBaseUrl.replace(/\/$/,""):null;
    const link=base?`${base}/browse/${key}`:null;
    return{_jiraKey:key,title:`[${key}] ${summary}`,description:description.slice(0,500),link};
  });
}

function JiraImportModal({onImport,onClose,criteria}){
  const [dragging,setDragging]=useState(false);
  const [error,setError]=useState("");
  const [parsedItems,setParsedItems]=useState([]);
  const [selected,setSelected]=useState(new Set());
  const [baseUrl,setBaseUrl]=useState("");
  const fileRef=useRef();

  const reset=()=>{setParsedItems([]);setSelected(new Set());setError("");setDragging(false)};

  const processFile=useCallback((file,url)=>{
    if(!file)return;
    if(!file.name.endsWith(".json")){setError("Seuls les fichiers .json sont acceptés.");return}
    const reader=new FileReader();
    reader.onload=e=>{
      try{
        const raw=JSON.parse(e.target.result);
        const items=parseJiraJson(raw,url);
        setParsedItems(items);
        setSelected(new Set(items.map((_,i)=>i)));
        setError("");
      }catch(err){setError(err.message);setParsedItems([])}
    };
    reader.readAsText(file);
  },[]);

  const onDrop=e=>{e.preventDefault();setDragging(false);processFile(e.dataTransfer.files[0],baseUrl)};
  const toggleItem=i=>setSelected(prev=>{const n=new Set(prev);n.has(i)?n.delete(i):n.add(i);return n});
  const allSelected=parsedItems.length>0&&selected.size===parsedItems.length;
  const toggleAll=()=>setSelected(allSelected?new Set():new Set(parsedItems.map((_,i)=>i)));

  const handleImport=()=>{
    const toImport=parsedItems
      .filter((_,i)=>selected.has(i))
      .map(({_jiraKey,...item})=>({
        id:uid(),
        title:item.title,
        description:item.description,
        link:item.link,
        criteria:criteria.map(c=>({...c})),
        status:"active",
        createdAt:Date.now(),
      }));
    onImport(toImport);
    onClose();
  };

  const S={
    overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24},
    modal:{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"var(--shadow)"},
    header:{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"},
    body:{padding:"16px 20px",overflowY:"auto",flex:1,display:"flex",flexDirection:"column",gap:14},
    footer:{padding:"12px 20px",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end",gap:8},
    dropzone:{border:`2px dashed ${dragging?"var(--accent)":error?"var(--red)":"var(--border2)"}`,borderRadius:"var(--r-lg)",padding:"28px 20px",textAlign:"center",cursor:"pointer",background:dragging?"var(--surface2)":"transparent",transition:"all 0.15s"},
    label:{fontSize:12,fontWeight:500,color:"var(--muted)",display:"block",marginBottom:4},
    previewItem:sel=>({background:sel?"var(--surface2)":"transparent",border:`1px solid ${sel?"var(--border2)":"var(--border)"}`,borderRadius:"var(--r)",padding:"8px 10px",cursor:"pointer",display:"flex",gap:8,alignItems:"flex-start",transition:"all 0.1s"}),
    errorBox:{background:"var(--red-bg)",border:"1px solid transparent",borderRadius:"var(--r)",padding:"8px 12px",color:"var(--red)",fontSize:12,fontFamily:"var(--mono)"},
    keyTag:{fontSize:11,fontFamily:"var(--mono)",color:"var(--green)",fontWeight:500,display:"block",marginBottom:2},
  };

  return(
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <p style={{fontWeight:600,fontSize:14,margin:0}}>Import Jira — JSON</p>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:18,lineHeight:1}}>✕</button>
        </div>
        <div style={S.body}>
          <div>
            <label style={S.label}>URL de base Jira <span style={{fontWeight:400}}>(optionnel, pour générer les liens)</span></label>
            <input value={baseUrl} onChange={e=>setBaseUrl(e.target.value)} placeholder="https://monentreprise.atlassian.net"/>
          </div>

          {parsedItems.length===0&&(
            <div style={S.dropzone}
              onDragOver={e=>{e.preventDefault();setDragging(true)}}
              onDragLeave={()=>setDragging(false)}
              onDrop={onDrop}
              onClick={()=>fileRef.current?.click()}
            >
              <p style={{fontSize:13,color:"var(--text2)",fontWeight:500,marginBottom:4}}>Déposer le fichier JSON Jira ici</p>
              <p style={{fontSize:12,color:"var(--muted)"}}>ou cliquer pour parcourir</p>
            </div>
          )}

          <input ref={fileRef} type="file" accept=".json" style={{display:"none"}} onChange={e=>processFile(e.target.files[0],baseUrl)}/>

          {error&&<div style={S.errorBox}>{error}</div>}

          {parsedItems.length===0&&!error&&(
            <details style={{fontSize:12,color:"var(--muted)"}}>
              <summary style={{cursor:"pointer",color:"var(--text2)",fontWeight:500}}>Comment exporter depuis Jira ?</summary>
              <ol style={{margin:"8px 0 0 16px",lineHeight:1.8}}>
                <li>Ouvre ton board ou filtre Jira</li>
                <li>Menu <strong>···</strong> → <strong>Exporter les issues</strong></li>
                <li>Choisir <strong>JSON</strong></li>
                <li>Dépose le fichier ici</li>
              </ol>
            </details>
          )}

          {parsedItems.length>0&&(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:12,fontWeight:500,color:"var(--muted)"}}>Issues détectées</span>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:11,fontFamily:"var(--mono)",color:"var(--green)",fontWeight:500}}>{selected.size}/{parsedItems.length}</span>
                  <button onClick={toggleAll} style={{background:"none",border:"none",fontSize:12,color:"var(--muted)",cursor:"pointer",textDecoration:"underline",fontFamily:"var(--font)",padding:0}}>
                    {allSelected?"Tout désélectionner":"Tout sélectionner"}
                  </button>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:240,overflowY:"auto"}}>
                {parsedItems.map((item,i)=>(
                  <div key={i} style={S.previewItem(selected.has(i))} onClick={()=>toggleItem(i)}>
                    <input type="checkbox" checked={selected.has(i)} onChange={()=>toggleItem(i)} onClick={e=>e.stopPropagation()} style={{marginTop:2,accentColor:"var(--accent)",flexShrink:0,cursor:"pointer"}}/>
                    <div style={{minWidth:0}}>
                      <span style={S.keyTag}>{item._jiraKey}</span>
                      <span style={{fontSize:13,color:"var(--text)",display:"block",lineHeight:1.4}}>{item.title.replace(/^\[.*?\] /,"")}</span>
                      {item.description&&<span style={{fontSize:11,color:"var(--muted)",display:"block",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.description}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={()=>{reset();fileRef.current?.click()}} style={{background:"none",border:"none",fontSize:12,color:"var(--muted)",cursor:"pointer",textDecoration:"underline",fontFamily:"var(--font)",padding:0,marginTop:8}}>
                ↩ Choisir un autre fichier
              </button>
            </div>
          )}
        </div>
        <div style={S.footer}>
          <Btn variant="ghost" size="sm" onClick={onClose}>Annuler</Btn>
          <Btn variant="primary" size="sm" disabled={selected.size===0} onClick={handleImport}>
            Importer {selected.size>0?`${selected.size} issue${selected.size>1?"s":""}`:""}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN BOARD ───────────────────────────────────────────────────────────────
function AdminBoard({state,setState,user}){
  const [showCreate,setShowCreate]=useState(false);
  const [editItem,setEditItem]=useState(null);
  const [showJiraImport,setShowJiraImport]=useState(false);
  const active=state.items.filter(it=>it.status!=="archived");
  const upd=(id,p)=>setState(s=>({...s,items:s.items.map(it=>it.id===id?{...it,...p}:it)}));
  const vote=(itemId,scores)=>setState(s=>{const prev=s.votes[itemId]??[];return{...s,votes:{...s.votes,[itemId]:[...prev.filter(v=>v.userId!==user.id),{userId:user.id,userName:user.name,scores}]}}});
  const modeToggleLabel=state.mode==="async"?"Passer en mode Direct":"Passer en mode Asynchrone";

  const handleJiraImport=newItems=>{
    setState(s=>({...s,items:[...s.items,...newItems]}));
  };

  return(
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 24px"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:28,gap:16,flexWrap:"wrap"}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em",marginBottom:4}}>{state.teamName||"Tableau"}</h1>
          <p style={{color:"var(--muted)",fontSize:12,fontFamily:"var(--mono)"}}>{active.length} fiche{active.length>1?"s":""} · {(state.participants||[]).length} participant{(state.participants||[]).length>1?"s":""} · {state.mode==="async"?"Asynchrone":"Direct (Live)"}</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn variant="subtle" size="sm" onClick={()=>setState(s=>({...s,mode:s.mode==="async"?"live":"async",resultsRevealed:false}))}>{modeToggleLabel}</Btn>
          {state.mode==="live"&&<Btn variant={state.resultsRevealed?"danger":"success"} size="sm" onClick={()=>setState(s=>({...s,resultsRevealed:!s.resultsRevealed}))}>{state.resultsRevealed?"Masquer les résultats":"Révéler les résultats"}</Btn>}
          <Btn variant="subtle" size="sm" onClick={()=>setShowJiraImport(true)}>↑ Importer Jira</Btn>
          <Btn variant="primary" size="sm" onClick={()=>setShowCreate(true)}>Nouvelle fiche</Btn>
        </div>
      </div>

      {(state.participants||[]).length>0&&(
        <div style={{marginBottom:20,padding:"10px 14px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:"var(--muted)",fontWeight:500,flexShrink:0}}>Connectés</span>
          {state.participants.map(p=><span key={p.id} className="tag" style={{background:"var(--surface)",border:"1px solid var(--border)",color:"var(--text2)"}}>{p.name}</span>)}
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {active.length===0&&(
          <div style={{textAlign:"center",padding:"60px 24px",color:"var(--muted)"}}>
            <p style={{fontSize:14,marginBottom:8}}>Aucune fiche active.</p>
            <Btn variant="subtle" size="sm" onClick={()=>setShowCreate(true)}>Créer la première fiche</Btn>
          </div>
        )}
        {active.map(item=>(
          <ItemCard key={item.id} item={item} votes={state.votes[item.id]||[]} conversion={state.conversion}
            revealed={state.mode==="async"||state.resultsRevealed} isAdmin userId={user.id} mode={state.mode}
            onVote={s=>vote(item.id,s)} onArchive={()=>upd(item.id,{status:"archived"})}
            onDelete={()=>setState(s=>({...s,items:s.items.filter(i=>i.id!==item.id),votes:Object.fromEntries(Object.entries(s.votes).filter(([k])=>k!==item.id))}))}
            onEdit={()=>setEditItem(item)} onMarkDiscussion={()=>upd(item.id,{status:"discussion"})} onMarkActive={()=>upd(item.id,{status:"active"})}
          />
        ))}
      </div>

      {showCreate&&<ItemModal onSave={d=>{setState(s=>({...s,items:[...s.items,{id:uid(),...d,criteria:s.criteria.map(c=>({...c})),status:"active",createdAt:Date.now()}]}));setShowCreate(false)}} onClose={()=>setShowCreate(false)}/>}
      {editItem&&<ItemModal initial={editItem} onSave={d=>{setState(s=>({...s,items:s.items.map(it=>it.id===editItem.id?{...it,...d}:it)}));setEditItem(null)}} onClose={()=>setEditItem(null)}/>}
      {showJiraImport&&<JiraImportModal criteria={state.criteria} onImport={handleJiraImport} onClose={()=>setShowJiraImport(false)}/>}
    </div>
  )
}

// ─── VOTER BOARD ───────────────────────────────────────────────────────────────
function VoterBoard({state,setState,user}){
  const active=state.items.filter(it=>it.status!=="archived");
  const revealed=state.mode==="async"||state.resultsRevealed;
  const upd=(id,p)=>setState(s=>({...s,items:s.items.map(it=>it.id===id?{...it,...p}:it)}));
  const vote=(itemId,scores)=>setState(s=>{const prev=s.votes[itemId]??[];return{...s,votes:{...s.votes,[itemId]:[...prev.filter(v=>v.userId!==user.id),{userId:user.id,userName:user.name,scores}]}}});
  return(
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 24px"}}>
      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em",marginBottom:4}}>Fiches à estimer</h1>
        <p style={{color:"var(--muted)",fontSize:12,fontFamily:"var(--mono)"}}>{state.mode==="live"?"Mode Direct":"Mode Asynchrone"} · {active.length} fiche{active.length>1?"s":""}</p>
      </div>
      {state.mode==="live"&&!revealed&&(
        <div style={{marginBottom:16,padding:"10px 14px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--r)",fontSize:13,color:"var(--text2)"}}>
          Les résultats seront révélés par l'administrateur.
        </div>
      )}
      {state.mode==="async"&&(
        <div style={{marginBottom:16,padding:"10px 14px",background:"var(--green-bg)",border:"1px solid var(--border)",borderRadius:"var(--r)",fontSize:13,color:"var(--green)",fontWeight:400}}>
          Mode Asynchrone — votez sur chaque fiche pour voir les résultats.
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {active.length===0&&<p style={{textAlign:"center",padding:"60px 0",color:"var(--muted)",fontSize:13}}>Aucune fiche active pour le moment.</p>}
        {active.map(item=><ItemCard key={item.id} item={item} votes={state.votes[item.id]||[]} conversion={state.conversion} revealed={revealed} isAdmin={false} userId={user.id} mode={state.mode} onVote={s=>vote(item.id,s)} onMarkDiscussion={()=>upd(item.id,{status:"discussion"})} onMarkActive={()=>upd(item.id,{status:"active"})}/>)}
      </div>
    </div>
  )
}

// ─── CONFIG ────────────────────────────────────────────────────────────────────
function ConfigSection({title,children}){
  return(
    <div style={{marginBottom:28}}>
      <p style={{fontSize:12,fontWeight:500,color:"var(--muted)",marginBottom:14,paddingBottom:8,borderBottom:"1px solid var(--border)"}}>{title}</p>
      {children}
    </div>
  );
}

function ConfigView({state,setState,sessionId}){
  const [criteria,setCriteria]=useState(state.criteria.map(c=>({...c,labels:{...c.labels}})));
  const [conversion,setConversion]=useState(state.conversion.map(r=>({...r,id:r.id??uid()})));
  const [teamName,setTeamName]=useState(state.teamName||"");
  const [saved,setSaved]=useState(false);
  const save=()=>{setState(s=>({...s,criteria,conversion,teamName}));setSaved(true);setTimeout(()=>setSaved(false),2000)};
  const base=`${window.location.origin}${window.location.pathname}`;
  const voterUrl=`${base}?s=${sessionId}`;
  const adminUrl=`${base}?s=${sessionId}&role=admin`;

  return(
    <div style={{maxWidth:680,margin:"0 auto",padding:"32px 24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:32}}>
        <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em"}}>Configuration</h1>
        <Btn variant={saved?"success":"primary"} size="sm" onClick={save}>{saved?"Sauvegardé":"Sauvegarder"}</Btn>
      </div>

      <ConfigSection title="Général">
        <Field label="Nom de l'équipe"><input value={teamName} onChange={e=>setTeamName(e.target.value)}/></Field>
      </ConfigSection>

      <ConfigSection title="Liens de la session">
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <label>Lien votants</label>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <code style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",background:"var(--surface2)",padding:"7px 10px",borderRadius:"var(--r)",border:"1px solid var(--border)",wordBreak:"break-all",flex:1,lineHeight:1.5}}>{voterUrl}</code>
              <CopyBtn text={voterUrl}/>
            </div>
          </div>
          <div>
            <label>Lien admin</label>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <code style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",background:"var(--surface2)",padding:"7px 10px",borderRadius:"var(--r)",border:"1px solid var(--border)",wordBreak:"break-all",flex:1,lineHeight:1.5}}>{adminUrl}</code>
              <CopyBtn text={adminUrl}/>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:"var(--muted)"}}>Code session :</span>
            <code style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:500,color:"var(--text)"}}>{sessionId}</code>
            <CopyBtn text={sessionId}/>
          </div>
        </div>
      </ConfigSection>

      <ConfigSection title={`Critères (${criteria.length})`}>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:10}}>
          {criteria.map((c,i)=>(
            <div key={c.id??i} style={{padding:14,border:"1px solid var(--border)",borderRadius:"var(--r-lg)",background:"var(--surface2)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <Field label={`Critère ${i+1}`} style={{flex:1,marginBottom:0,marginRight:10}}>
                  <input value={c.name} onChange={e=>setCriteria(prev=>prev.map((x,j)=>j===i?{...x,name:e.target.value}:x))} placeholder="Ex: Architecture"/>
                </Field>
                {criteria.length>1&&<button onClick={()=>setCriteria(prev=>prev.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,flexShrink:0,marginTop:18}}>✕</button>}
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {T_SHIRT.map(t=>(
                  <Field key={t.key} label={`Label ${t.key}`} style={{flex:1,minWidth:100}}>
                    <input value={c.labels?.[t.key]??""} onChange={e=>setCriteria(prev=>prev.map((x,j)=>j===i?{...x,labels:{...x.labels,[t.key]:e.target.value}}:x))} placeholder="Optionnel"/>
                  </Field>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Btn variant="ghost" size="sm" onClick={()=>setCriteria(prev=>[...prev,{id:uid(),name:"",labels:{S:"",M:"",L:""}}])}>+ Ajouter un critère</Btn>
      </ConfigSection>

      <ConfigSection title="Table Score → Story Points">
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
          {conversion.map((row,i)=>(
            <div key={row.id} style={{display:"flex",gap:8,alignItems:"center"}}>
              <Field label="Score max" style={{flex:1}}><input type="number" value={row.maxLoe} onChange={e=>setConversion(prev=>prev.map((r,j)=>j===i?{...r,maxLoe:Number(e.target.value)}:r))} min={0}/></Field>
              <Field label="Story Points" style={{flex:1}}><input type="number" value={row.sp} onChange={e=>setConversion(prev=>prev.map((r,j)=>j===i?{...r,sp:Number(e.target.value)}:r))} min={0}/></Field>
              <button onClick={()=>setConversion(prev=>prev.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,flexShrink:0,marginTop:18}}>✕</button>
            </div>
          ))}
        </div>
        <Btn variant="ghost" size="sm" onClick={()=>setConversion(prev=>[...prev,{id:uid(),maxLoe:0,sp:0}])}>+ Ajouter un palier</Btn>
      </ConfigSection>
    </div>
  )
}

// ─── ARCHIVE ───────────────────────────────────────────────────────────────────
function ArchiveView({state,setState}){
  const archived=state.items.filter(it=>it.status==="archived");
  return(
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 24px"}}>
      <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em",marginBottom:4}}>Archives</h1>
      <p style={{color:"var(--muted)",fontSize:12,marginBottom:28,fontFamily:"var(--mono)"}}>{archived.length} fiche{archived.length>1?"s":""} · suppression automatique après 48h</p>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {archived.length===0&&<p style={{textAlign:"center",padding:"60px 0",color:"var(--muted)",fontSize:13}}>Aucune fiche archivée.</p>}
        {archived.map(item=>{
          const result=calcItemResult(item,state.votes[item.id]||[],state.conversion);
          return(
            <div key={item.id} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,opacity:0.7}}>
              <div>
                <p style={{fontWeight:500,fontSize:14,marginBottom:4}}>{item.title}</p>
                {result&&<div style={{display:"flex",gap:8}}>
                  <span className="tag" style={{background:"var(--surface2)",color:"var(--text2)",border:"1px solid var(--border)"}}>Score {result.totalLoe}</span>
                  <span className="tag" style={{background:"var(--green-bg)",color:"var(--green)"}}>→ {result.sp} SP</span>
                </div>}
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <Btn variant="ghost" size="xs" onClick={()=>setState(s=>({...s,items:s.items.map(it=>it.id===item.id?{...it,status:"active"}:it)}))}>Restaurer</Btn>
                <Btn variant="danger" size="xs" onClick={()=>setState(s=>({...s,items:s.items.filter(it=>it.id!==item.id),votes:Object.fromEntries(Object.entries(s.votes).filter(([k])=>k!==item.id))}))}>Supprimer</Btn>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── SUPER ADMIN ───────────────────────────────────────────────────────────────
const SUPER_ADMIN_KEY = process.env.REACT_APP_SUPER_ADMIN_KEY || "";

function SuperAdminPage(){
  const [authed,setAuthed]=useState(false);
  const [pwd,setPwd]=useState("");
  const [err,setErr]=useState("");
  const [sessions,setSessions]=useState([]);
  const [loading,setLoading]=useState(false);
  const [confirmId,setConfirmId]=useState(null); // id session à supprimer
  const [confirmName,setConfirmName]=useState("");
  const [superKeyInput,setSuperKeyInput]=useState("");
  const [deleteErr,setDeleteErr]=useState("");
  const [deleting,setDeleting]=useState(false);

  function login(){
    if(!SUPER_ADMIN_KEY||pwd===SUPER_ADMIN_KEY){setAuthed(true);loadSessions()}
    else{setErr("Clé incorrecte.")}
  }

  async function loadSessions(){
    setLoading(true);
    try{const r=await api("list","GET");setSessions(r.sessions||[])}
    catch{setErr("Impossible de charger les sessions.")}
    finally{setLoading(false)}
  }

  async function handleDelete(){
    if(!confirmId||!superKeyInput.trim()){setDeleteErr("Clé super-admin requise.");return}
    setDeleting(true);setDeleteErr("");
    try{
      await api("superdelete","DELETE",{superAdminKey:superKeyInput.trim()},confirmId);
      setSessions(s=>s.filter(x=>x.id!==confirmId));
      setConfirmId(null);setSuperKeyInput("");
    }catch(e){setDeleteErr("Clé super-admin incorrecte ou erreur serveur.")}
    finally{setDeleting(false)}
  }

  function fmtSize(bytes){
    if(!bytes)return "—";
    if(bytes<1024)return bytes+" o";
    return Math.round(bytes/1024)+" Ko";
  }
  function fmtDate(d){
    if(!d)return "—";
    return new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
  }

  if(!authed){
    return(
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:340}}>
          <p style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--muted)",marginBottom:24,letterSpacing:"0.05em"}}>SUPER ADMIN</p>
          <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em",marginBottom:24}}>Administration globale</h1>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Field label="Clé super-admin">
              <input type="password" value={pwd} onChange={e=>setPwd(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} autoFocus placeholder="••••••••"/>
            </Field>
            {err&&<p style={{color:"var(--red)",fontSize:12}}>{err}</p>}
            <Btn variant="primary" size="md" onClick={login} style={{width:"100%"}}>Accéder →</Btn>
          </div>
        </div>
      </div>
    )
  }

  return(
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 24px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:28,gap:16,flexWrap:"wrap"}}>
        <div>
          <p style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--muted)",marginBottom:4,letterSpacing:"0.05em"}}>SUPER ADMIN</p>
          <h1 style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em"}}>Sessions actives</h1>
        </div>
        <Btn variant="subtle" size="sm" onClick={loadSessions} disabled={loading}>{loading?"Chargement...":"↻ Rafraîchir"}</Btn>
      </div>

      {sessions.length===0&&!loading&&(
        <p style={{textAlign:"center",padding:"60px 0",color:"var(--muted)",fontSize:13}}>Aucune session trouvée.</p>
      )}

      {sessions.length>0&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {sessions.map(s=>(
            <div key={s.id} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:"14px 16px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontWeight:600,fontSize:14,marginBottom:2}}>{s.name}</p>
                <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                  <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--muted)"}}>{s.id}</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>Créée le {fmtDate(s.created_at)}</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>État : {fmtSize(s.state_size)}</span>
                </div>
              </div>
              <Btn variant="danger" size="xs" onClick={()=>{setConfirmId(s.id);setConfirmName(s.name);setSuperKeyInput("");setDeleteErr("")}}>Supprimer</Btn>
            </div>
          ))}
        </div>
      )}

      {/* Modale de confirmation suppression */}
      {confirmId&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget){setConfirmId(null);setSuperKeyInput("");setDeleteErr("")}}}>
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:24,width:"100%",maxWidth:420,boxShadow:"var(--shadow)"}} onClick={e=>e.stopPropagation()}>
            <p style={{fontWeight:600,fontSize:15,marginBottom:6}}>Supprimer la session ?</p>
            <p style={{color:"var(--text2)",fontSize:13,marginBottom:4,lineHeight:1.5}}>
              <strong>{confirmName}</strong>
            </p>
            <p style={{color:"var(--muted)",fontSize:12,marginBottom:16,lineHeight:1.5}}>
              Cette action est irréversible. Toutes les fiches et votes seront perdus. Entrez votre clé super-admin pour confirmer.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Field label="Votre clé super-admin">
                <input type="password" value={superKeyInput} onChange={e=>setSuperKeyInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleDelete()} placeholder="••••••••" autoFocus/>
              </Field>
              {deleteErr&&<p style={{color:"var(--red)",fontSize:12}}>{deleteErr}</p>}
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn variant="ghost" size="sm" onClick={()=>{setConfirmId(null);setSuperKeyInput("");setDeleteErr("")}}>Annuler</Btn>
                <Btn variant="danger" size="sm" disabled={deleting||!superKeyInput.trim()} onClick={handleDelete}>{deleting?"Suppression...":"Supprimer définitivement"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── APP ROOT ──────────────────────────────────────────────────────────────────
export default function App(){
  const [page,setPage]=useState("landing");
  const [sessionId,setSessionId]=useState(null);
  const [sessionName,setSessionName]=useState("");
  const [user,setUser]=useState(null);
  const [view,setView]=useState("board");
  const [appState,setAppStateLocal]=useState(null);
  const [syncStatus,setSyncStatus]=useState("synced");
  const [theme,setTheme]=useState("light");
  const pendingRef=useRef(false);

  useEffect(()=>{document.documentElement.setAttribute("data-theme",theme)},[theme]);

  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const urlSid=params.get("s");
    // ── Accès super admin via ?admin=1 ────────────────────────────────────────
    const isAdmin=params.get("admin")==="1";
    if(isAdmin){setPage("superadmin");return}
    if(urlSid){
      setSessionId(urlSid);setPage("checking");
      api("check","GET",null,urlSid).then(r=>{setSessionName(r.name);setPage("login")}).catch(()=>setPage("landing"));
      return;
    }
    const saved=getSavedSession();
    if(saved){
      setSessionId(saved.sessionId);setSessionName(saved.sessionName);
      setUser({id:saved.userId,name:saved.userName,role:saved.role});
      setPage("app");
      window.history.replaceState({},"",`?s=${saved.sessionId}`);
    }
  },[]);

  useEffect(()=>{
    if(!sessionId||!user)return;
    setSyncStatus("syncing");
    api("get","GET",null,sessionId).then(data=>{
      if(data.__empty){const init=makeInit(sessionName);setAppStateLocal(init);api("set","POST",init,sessionId)}
      else setAppStateLocal(data);
      setSyncStatus("synced");
    }).catch(()=>setSyncStatus("error"));
  },[sessionId,user]);

  useEffect(()=>{
    if(!sessionId||!user||!appState)return;
    const t=setInterval(async()=>{
      if(pendingRef.current)return;
      try{
        const remote=await api("get","GET",null,sessionId);
        if(!remote.__empty){
          setAppStateLocal(prev=>{
            const mv={...remote.votes};
            for(const[id,vl]of Object.entries(prev?.votes||{})){if(!mv[id])mv[id]=[];for(const v of vl)if(!mv[id].find(r=>r.userId===v.userId))mv[id].push(v)}
            return{...remote,votes:mv}
          });
        }
        setSyncStatus("synced");
      }catch{setSyncStatus("error")}
    },POLL_MS);
    return()=>clearInterval(t);
  },[sessionId,user,!!appState]);

  const setState=useCallback((updater)=>{
    setAppStateLocal(prev=>{
      const next=typeof updater==="function"?updater(prev):updater;
      pendingRef.current=true;setSyncStatus("syncing");
      api("set","POST",next,sessionId).then(()=>setSyncStatus("synced")).catch(()=>setSyncStatus("error")).finally(()=>{pendingRef.current=false});
      return next;
    });
  },[sessionId]);

  function handleLogin(u){
    setUser(u);setPage("app");
    saveSession({sessionId,sessionName,userId:u.id,userName:u.name,role:u.role});
    setTimeout(()=>setState(s=>s?({...s,participants:[...(s.participants||[]).filter(p=>p.name!==u.name),{id:u.id,name:u.name}]}):s),600);
  }

  function handleLogout(){
    if(appState)setState(s=>({...s,participants:(s.participants||[]).filter(p=>p.id!==user?.id)}));
    clearSession();
    setUser(null);setAppStateLocal(null);setView("board");setPage("landing");setSessionId(null);setSessionName("");
    window.history.replaceState({},"",window.location.pathname);
  }

  const Loader=()=>(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <span style={{fontFamily:"var(--mono)",fontSize:13,color:"var(--muted)"}}>Chargement...</span>
    </div>
  );

  const W=c=><><style>{GLOBAL_STYLE}</style>{c}</>;

  if(page==="superadmin") return W(<SuperAdminPage/>);
  if(page==="landing") return W(<LandingPage onEnterApp={()=>setPage("home")}/>);
  if(page==="checking") return W(<Loader/>);
  if(page==="home")    return W(<HomePage onJoin={(t,n)=>{setSessionId(t);setSessionName(n);setPage("login");window.history.replaceState({},"",`?s=${t}`)}} onCreate={()=>setPage("create")} onBack={()=>setPage("landing")}/>);
  if(page==="create")  return W(<CreateSessionPage onCreated={(t,n)=>{setSessionId(t);setSessionName(n);setPage("login");window.history.replaceState({},"",`?s=${t}`)}} onBack={()=>setPage("home")}/>);
  if(page==="login")   return W(<LoginPage sessionId={sessionId} sessionName={sessionName} onLogin={handleLogin} onBack={()=>{setPage("home");window.history.replaceState({},"",window.location.pathname)}}/>);
  if(!appState)        return W(<Loader/>);

  return W(
    <div style={{minHeight:"100vh"}}>
      <TopNav user={user} sessionId={sessionId} view={view} setView={setView} onLogout={handleLogout} syncStatus={syncStatus} theme={theme} onToggleTheme={()=>setTheme(t=>t==="dark"?"light":"dark")}/>
      {view==="board"  &&user.role==="admin"&&<AdminBoard  state={appState} setState={setState} user={user}/>}
      {view==="board"  &&user.role==="voter"&&<VoterBoard  state={appState} setState={setState} user={user}/>}
      {view==="config" &&user.role==="admin"&&<ConfigView  state={appState} setState={setState} sessionId={sessionId}/>}
      {view==="archive"&&user.role==="admin"&&<ArchiveView state={appState} setState={setState}/>}
    </div>
  );
}
